import { useState } from "react";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faCircleCheck,
  faFileLines,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { CC_ERROR_TEXT, CC_PANEL, CC_SECTION_LABEL } from "@/components/case-checking/caseCheckingChrome";
import { AgentHumanGradeCompare } from "@/components/case-checking/humanReviewCompare";
import { useHumanReviewOutcome } from "@/components/case-checking/useHumanReviewOutcome";
import { supportsFileReviewExport } from "@/components/case-checking/agenticCaseCheck";
import {
  caseReviewRollupGrade,
  ICON_CHECK_PASSED,
  type CaseCheckingGrade,
} from "@/components/case-checking/caseCheckingStatusBadges";
import { Button } from "@/components/ui/button/Button";
import {
  downloadAdviceQualityFileReviewDocx,
  downloadGuidedReviewAuditCsv,
  type CaseReview as ApiCaseReview,
} from "@/services/api/case-checking";

/** Guided review final step — generate file review artefact, link out to Case Summary. */
export function ReviewCaseSummaryStep({
  caseId,
  clientName,
  caseReview,
  fallbackGrade,
  displayAdviser,
  psRunId,
  reviewRunId,
  onGoToSummary,
  companyId,
}: {
  caseId: string;
  clientName?: string;
  caseReview: ApiCaseReview | null;
  fallbackGrade: CaseCheckingGrade;
  displayAdviser?: string;
  psRunId?: string;
  reviewRunId?: string;
  onGoToSummary: () => void;
  companyId?: string;
}) {
  const {
    loading,
    outcomeGrade,
    agentGrade,
    ruleRollupGrade,
    reviewAttribution,
  } = useHumanReviewOutcome(caseId, caseReview, fallbackGrade, displayAdviser, companyId);
  const [exporting, setExporting] = useState(false);
  const [auditExporting, setAuditExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const grade = outcomeGrade ?? caseReviewRollupGrade(caseReview, fallbackGrade);
  const fileReviewAvailable = supportsFileReviewExport(companyId);

  const handleGenerate = async () => {
    setExporting(true);
    setExportError(null);
    const safeClient = (clientName || "case").replace(/[^A-Za-z0-9_-]+/g, "_").slice(0, 60);
    try {
      await downloadAdviceQualityFileReviewDocx(
        caseId,
        `advice_quality_file_review_${safeClient}.docx`,
        psRunId,
        reviewRunId,
      );
    } catch (e) {
      setExportError(e instanceof Error ? e.message : "Failed to generate file review");
    } finally {
      setExporting(false);
    }
  };

  const handleAuditExport = async () => {
    if (!reviewRunId) return;
    setAuditExporting(true);
    setExportError(null);
    try {
      await downloadGuidedReviewAuditCsv(caseId, reviewRunId, `guided_review_audit_${caseId.slice(-6)}.csv`);
    } catch (e) {
      setExportError(e instanceof Error ? e.message : "Failed to export audit log");
    } finally {
      setAuditExporting(false);
    }
  };

  if (loading) {
    return <p className="text-sm text-slate-400">Loading case summary…</p>;
  }

  const reviewedOn = reviewAttribution?.reviewedAt
    ? new Date(reviewAttribution.reviewedAt).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;
  const reviewer = reviewAttribution?.reviewerName?.trim();

  return (
    <div className="space-y-4" data-testid="review-case-summary-step">
      <div
        className={clsx(
          CC_PANEL,
          "flex items-start gap-3 border-emerald-500/20 bg-emerald-500/[0.06]",
        )}
        data-testid="review-workflow-complete-banner"
      >
        <FontAwesomeIcon icon={faCircleCheck} className={clsx("mt-0.5 h-5 w-5 shrink-0", ICON_CHECK_PASSED)} />
        <div>
          <p className="text-sm font-medium text-emerald-800 dark:text-emerald-100">Guided review complete</p>
          <p className="mt-0.5 text-sm text-emerald-700/90 dark:text-emerald-200/80">
            {fileReviewAvailable
              ? "Actions approved — generate the file review document or open Case Summary for the full breakdown."
              : "Actions approved — open Case Summary for the full breakdown."}
          </p>
        </div>
      </div>

      <div className={CC_PANEL}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 space-y-1">
            <p className={CC_SECTION_LABEL}>File review artefact</p>
            <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Advice Quality — File Review</h3>
            <p className="max-w-2xl text-sm text-slate-400 leading-relaxed">
              {fileReviewAvailable
                ? "Word document with your overall grade, role grades, completeness, remedial actions, inconsistencies, and human rule overrides."
                : "Coming soon — a file review template for your firm is in progress. Your assessment is saved and available in Case Summary."}
            </p>
            {reviewedOn ? (
              <p className="text-xs text-slate-500">
                Assessment saved on {reviewedOn}
                {reviewer ? ` · ${reviewer}` : ""}
              </p>
            ) : null}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              color="indigo"
              icon={<FontAwesomeIcon icon={faFileLines} />}
              onClick={() => void handleGenerate()}
              disabled={exporting || !fileReviewAvailable}
              data-testid="review-generate-file-review-docx"
            >
              {!fileReviewAvailable ? "Coming soon" : exporting ? "Generating…" : "Generate file review"}
            </Button>
            {reviewRunId ? (
              <Button
                outline
                onClick={() => void handleAuditExport()}
                disabled={auditExporting}
                data-testid="review-export-audit-csv"
              >
                {auditExporting ? "Exporting…" : "Export audit log"}
              </Button>
            ) : null}
          </div>
        </div>
        {exportError ? <p className={clsx("mt-2 text-xs", CC_ERROR_TEXT)}>{exportError}</p> : null}
      </div>

      <div className={CC_PANEL}>
        <p className={CC_SECTION_LABEL}>Overall outcome</p>
        <AgentHumanGradeCompare
          agentGrade={agentGrade}
          humanGrade={grade}
          ruleRollupGrade={ruleRollupGrade}
        />
        <p className="mt-3 max-w-2xl text-sm text-slate-400 leading-relaxed">
          Statement, rule, and action detail — with agent vs your review — lives in Case Summary.
        </p>
        <div className="mt-4">
          <Button
            outline
            onClick={onGoToSummary}
            data-testid="review-go-to-case-summary"
            icon={<FontAwesomeIcon icon={faArrowRight} className="ml-1" />}
            iconPosition="right"
          >
            Go to Case Summary
          </Button>
        </div>
      </div>
    </div>
  );
}
